import type { Snapshot } from "@app/protocol";
import type { DiscordSDK } from "@discord/embedded-app-sdk";

/**
 * Rich Presence (Portal → Rich Presence): what a player's profile says while
 * the Activity is open. Asset keys match the Portal uploads: `logo` and one
 * per game. Pure builder + a thin sender that only talks when the text changed.
 */
export interface Presence {
  details: string;
  state: string;
  partySize: number;
  variant: Snapshot["table"]["settings"]["variant"];
  tableId: string;
}

const GAME_NAME: Record<Presence["variant"], string> = {
  batak: "Batak",
  spades: "Spades",
  tarneeb: "Tarneeb",
  baloot: "Baloot",
  hearts: "Hearts",
  king: "King",
  pisti: "Pishti",
};

/** Presence text is English on purpose: it shows on other people's screens, in their locale. */
export function presenceFor(snapshot: Snapshot): Presence {
  const { table, view } = snapshot;
  const seated = table.seats.filter((seat) => seat !== null).length;
  const hand =
    typeof view === "object" &&
    view !== null &&
    typeof (view as { hand?: unknown }).hand === "number"
      ? (view as { hand: number }).hand
      : null;
  const target =
    table.settings.goal === null
      ? `${table.settings.length} hands`
      : `first to ${table.settings.goal}`;
  let state: string;
  if (table.status === "playing" && hand !== null) state = `Hand ${hand} · ${target}`;
  else if (table.status === "finished") state = "Match over";
  else state = seated === 0 ? "Opening a table" : `In the lobby · ${target}`;
  return {
    details: GAME_NAME[table.settings.variant],
    state,
    partySize: seated,
    variant: table.settings.variant,
    tableId: table.id,
  };
}

let lastSent = "";
let startedAt: number | null = null;

/** Sends the presence to Discord when it changed; failures are silent (presence is decoration). */
export async function pushPresence(
  sdk: DiscordSDK | null,
  presence: Presence,
  now: number,
): Promise<void> {
  const key = JSON.stringify(presence);
  if (sdk === null || key === lastSent) return;
  lastSent = key;
  startedAt ??= now;
  try {
    await sdk.commands.setActivity({
      activity: {
        type: 0,
        details: presence.details,
        state: presence.state,
        timestamps: { start: startedAt },
        party: { id: presence.tableId, size: [Math.max(1, presence.partySize), 4] },
        assets: {
          large_image: "logo",
          large_text: "High Card Club",
          small_image: presence.variant,
          small_text: presence.details,
        },
      },
    });
  } catch {
    // Older clients or a missing scope: the game goes on without a presence card.
    lastSent = "";
  }
}
